import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../api/client.ts";
import type { Block, BlockDocument, BlockType, Brand, MergeField, Template } from "../api/types.ts";
import { BlockCard } from "../components/composer/BlockCard.tsx";
import {
  BLOCK_LABELS,
  BLOCK_ORDER,
  newBlock,
  starterDocument,
} from "../components/composer/blocks.ts";
import { Preview } from "../components/composer/Preview.tsx";
import { confirmAction, toast } from "../components/feedback.tsx";
import { TestSendButton } from "../components/TestSendButton.tsx";
import { useUnsavedChanges } from "../components/unsaved.ts";

/** Offered by default so most templates never need a merge field configured. */
const SUGGESTED_FIELDS: MergeField[] = [
  { key: "first_name", label: "First name", path: "contact.first_name", fallback: "there" },
  { key: "last_name", label: "Last name", path: "contact.last_name", fallback: "" },
  { key: "company", label: "Company", path: "contact.company", fallback: "your team" },
  { key: "title", label: "Job title", path: "contact.title", fallback: "" },
];

export function ComposerPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isNew = id === "new";

  const brands = useQuery({
    queryKey: ["brands"],
    queryFn: () => api.get<Brand[]>("/config/brands"),
  });
  const existing = useQuery({
    queryKey: ["template", id],
    queryFn: () => api.get<Template>(`/templates/${id}`),
    enabled: !isNew,
  });

  const [name, setName] = useState("Untitled template");
  const [subject, setSubject] = useState("");
  const [previewText, setPreviewText] = useState("");
  const [brandId, setBrandId] = useState("");
  const [doc, setDoc] = useState<BlockDocument>(starterDocument);
  const [rawMjml, setRawMjml] = useState("");
  const [rawHtml, setRawHtml] = useState("");
  const [mergeFields, setMergeFields] = useState<MergeField[]>(SUGGESTED_FIELDS);
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mode = existing.data?.mode ?? "blocks";

  /**
   * Unsaved edits: the editable state now, against what was last loaded or
   * saved. Compared as a whole rather than flagged on change, so undoing an
   * edit by hand clears it.
   */
  const current = JSON.stringify({
    name,
    subject,
    previewText,
    doc,
    rawMjml,
    rawHtml,
    mergeFields,
  });
  const [savedState, setSavedState] = useState(current);
  const [snapshotDue, setSnapshotDue] = useState(false);
  useEffect(() => {
    if (snapshotDue) {
      setSavedState(current);
      setSnapshotDue(false);
    }
  }, [snapshotDue, current]);
  const unsaved = current !== savedState;
  useUnsavedChanges(unsaved);

  useEffect(() => {
    const t = existing.data;
    if (!t) return;
    setName(t.name);
    setSubject(t.subject);
    setPreviewText(t.previewText ?? "");
    setBrandId(t.brandId);
    if (t.document) setDoc(t.document);
    if (t.mergeFields?.length) setMergeFields(t.mergeFields);
    setRawMjml((t as Template & { rawMjml?: string }).rawMjml ?? "");
    setRawHtml((t as Template & { rawHtml?: string }).rawHtml ?? "");
    // Taken on the next render, once the values above have landed.
    setSnapshotDue(true);
  }, [existing.data]);

  useEffect(() => {
    if (!brandId && brands.data?.[0]) setBrandId(brands.data[0].id);
  }, [brandId, brands.data]);

  /**
   * Preview is compiled server-side rather than in the browser: MJML is the
   * same compiler the send path uses, and a second implementation here would
   * eventually disagree with what actually goes out.
   */
  const preview = useMutation({
    mutationFn: (body: unknown) =>
      api.post<{
        html: string;
        text: string;
        subject: string;
        previewText: string | null;
        errors: string[];
        undeclared: string[];
      }>("/templates/preview", body),
  });

  const previewBody = useMemo(
    () =>
      mode === "html"
        ? { rawHtml, subject, previewText, mergeFields }
        : mode === "raw"
          ? { rawMjml, subject, previewText, mergeFields }
          : { document: doc, subject, previewText, mergeFields },
    [mode, rawHtml, rawMjml, doc, subject, previewText, mergeFields],
  );

  const requestPreview = preview.mutate;
  useEffect(() => {
    // Debounced: every keystroke would otherwise be an MJML compile.
    const timer = setTimeout(() => requestPreview(previewBody), 400);
    return () => clearTimeout(timer);
  }, [previewBody, requestPreview]);

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        name,
        subject,
        previewText: previewText.trim() || null,
        mergeFields,
        ...(mode === "html" ? { rawHtml } : mode === "raw" ? { rawMjml } : { document: doc }),
      };
      return isNew
        ? api.post<Template>("/templates", { ...body, brandId })
        : api.patch<Template>(`/templates/${id}`, body);
    },
    onSuccess: (saved) => {
      setError(null);
      setSavedState(current);
      void qc.invalidateQueries({ queryKey: ["templates"] });
      if (isNew) navigate(`/templates/${saved.id}`, { replace: true });
    },
    onError: (err: Error) => setError(err.message),
  });

  /**
   * Checked here as well as by the API so the message says what to do and
   * the cursor lands in the field, rather than a round trip ending in an error
   * banner. The subject stays required: nothing later stops a template without
   * one from going out, and an email with no subject is the one mistake every
   * recipient sees.
   */
  const subjectRef = useRef<HTMLInputElement>(null);
  function onSave() {
    if (!name.trim()) {
      setError("Give the template a name before saving.");
      return;
    }
    if (!subject.trim()) {
      setError("Add a subject before saving.");
      subjectRef.current?.focus();
      return;
    }
    setError(null);
    save.mutate();
  }

  const eject = useMutation({
    mutationFn: () => api.post<Template>(`/templates/${id}/eject`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["template", id] });
      toast("Ejected to MJML");
    },
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = doc.blocks.findIndex((b) => b.id === active.id);
    const to = doc.blocks.findIndex((b) => b.id === over.id);
    const blocks = [...doc.blocks];
    const [moved] = blocks.splice(from, 1);
    blocks.splice(to, 0, moved!);
    setDoc({ ...doc, blocks });
  }

  const updateBlock = (next: Block) =>
    setDoc({ ...doc, blocks: doc.blocks.map((b) => (b.id === next.id ? next : b)) });

  const addBlock = (type: BlockType) => {
    const block = newBlock(type);
    // Before the footer, which belongs last.
    const footerAt = doc.blocks.findIndex((b) => b.type === "footer");
    const blocks = [...doc.blocks];
    blocks.splice(footerAt === -1 ? blocks.length : footerAt, 0, block);
    setDoc({ ...doc, blocks });
    setSelected(block.id);
  };

  return (
    <div className="page composer-page">
      <div className="composer-bar">
        <input
          className="title-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-label="Template name"
        />
        <div className="spacer" />
        {/* Tests the saved version: save first to test an edit. */}
        {!isNew && id && <TestSendButton templateId={id} />}
        {mode === "blocks" && !isNew && (
          <button
            type="button"
            className="btn ghost small"
            onClick={async () => {
              if (
                await confirmAction({
                  title: "Eject to MJML?",
                  message:
                    "This template becomes hand-written MJML and cannot be converted back to blocks.",
                  confirmLabel: "Eject to MJML",
                  destructive: true,
                })
              )
                eject.mutate();
            }}
          >
            Eject to MJML
          </button>
        )}
        <button className="btn" type="button" disabled={save.isPending} onClick={onSave}>
          {save.isPending ? "Saving…" : unsaved ? "Save changes" : "Saved"}
        </button>
      </div>

      {error && <div className="error">{error}</div>}
      {mode === "raw" && (
        <div className="notice">
          This template was ejected to MJML. The visual editor is no longer available for it —
          arbitrary MJML cannot be read back into blocks.
        </div>
      )}
      {mode === "html" && (
        <div className="notice">
          Imported HTML, sent exactly as it is here — nothing recompiles it. Replace the file or
          edit the source directly; merge tags still render per recipient.
        </div>
      )}
      {preview.data?.undeclared.length ? (
        <div className="notice warn">
          Used but not declared: {preview.data.undeclared.map((f) => `{{${f}}}`).join(", ")}. These
          render empty with no fallback.
        </div>
      ) : null}
      {preview.data?.errors.length ? (
        <div className="error">{preview.data.errors.join("; ")}</div>
      ) : null}

      <div className="composer-grid">
        <div>
          <div className="card">
            <div className="card-body">
              <div className="row">
                {/* Full width: a subject is a sentence, and half a row cut most of them off. */}
                <div className="field" style={{ gridColumn: "1 / -1" }}>
                  <label htmlFor="c-subject">Subject</label>
                  <input
                    id="c-subject"
                    ref={subjectRef}
                    required
                    aria-invalid={error !== null && !subject.trim()}
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Hi {{first_name}}, …"
                  />
                </div>
                <div className="field">
                  <label htmlFor="c-preview-text">Preview text</label>
                  <input
                    id="c-preview-text"
                    value={previewText}
                    onChange={(e) => setPreviewText(e.target.value)}
                    placeholder="The line after the subject in the inbox"
                  />
                </div>
                <div className="field">
                  <label htmlFor="c-brand">Brand</label>
                  <select
                    id="c-brand"
                    value={brandId}
                    onChange={(e) => setBrandId(e.target.value)}
                    disabled={!isNew}
                  >
                    {brands.data?.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {mode === "html" ? (
            <div className="card">
              <div className="card-body">
                <div className="field">
                  <div className="row" style={{ alignItems: "center", marginBottom: 6 }}>
                    <label htmlFor="c-html">HTML source</label>
                    <div className="spacer" />
                    <label className="btn ghost small" style={{ cursor: "pointer" }}>
                      Replace from file
                      <input
                        type="file"
                        accept=".html,.htm,text/html"
                        style={{ display: "none" }}
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (file) setRawHtml(await file.text());
                          // Cleared so choosing the same file twice still fires.
                          e.target.value = "";
                        }}
                      />
                    </label>
                  </div>
                  <textarea
                    id="c-html"
                    className="mono"
                    rows={24}
                    value={rawHtml}
                    onChange={(e) => setRawHtml(e.target.value)}
                  />
                </div>
              </div>
            </div>
          ) : mode === "raw" ? (
            <div className="card">
              <div className="card-body">
                <div className="field">
                  <label htmlFor="c-mjml">MJML source</label>
                  <textarea
                    id="c-mjml"
                    className="mono"
                    rows={24}
                    value={rawMjml}
                    onChange={(e) => setRawMjml(e.target.value)}
                  />
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="palette">
                {BLOCK_ORDER.map((type) => (
                  <button
                    key={type}
                    type="button"
                    className="btn ghost small"
                    onClick={() => addBlock(type)}
                  >
                    + {BLOCK_LABELS[type]}
                  </button>
                ))}
              </div>

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={onDragEnd}
              >
                <SortableContext
                  items={doc.blocks.map((b) => b.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {doc.blocks.map((block) => (
                    <BlockCard
                      key={block.id}
                      block={block}
                      selected={selected === block.id}
                      onSelect={() => setSelected(selected === block.id ? null : block.id)}
                      onChange={updateBlock}
                      onRemove={() =>
                        setDoc({ ...doc, blocks: doc.blocks.filter((b) => b.id !== block.id) })
                      }
                    />
                  ))}
                </SortableContext>
              </DndContext>

              {doc.blocks.length === 0 && <div className="empty">Add a block to start.</div>}
            </>
          )}

          <MergeFields fields={mergeFields} onChange={setMergeFields} />
        </div>

        <Preview
          html={preview.data?.html ?? ""}
          text={preview.data?.text ?? ""}
          subject={preview.data?.subject ?? subject}
          previewText={preview.data?.previewText ?? previewText}
          from={brands.data?.find((b) => b.id === brandId)?.name}
        />
      </div>
    </div>
  );
}

function MergeFields({
  fields,
  onChange,
}: {
  fields: MergeField[];
  onChange: (next: MergeField[]) => void;
}) {
  return (
    <div className="card">
      <div className="card-head">
        <div>
          <h3>Merge fields</h3>
          <p>
            Every field needs a fallback — an empty one is the clearest sign of a generated mail.
          </p>
        </div>
      </div>
      <table>
        <thead>
          <tr>
            <th>Tag</th>
            <th>Reads</th>
            <th>Fallback</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {fields.map((field, i) => (
            <tr key={field.key}>
              <td className="mono">{`{{${field.key}}}`}</td>
              <td className="mono" style={{ color: "var(--gray-500)" }}>
                {field.path}
              </td>
              <td>
                <input
                  value={field.fallback}
                  placeholder="(required)"
                  onChange={(e) => {
                    const next = [...fields];
                    next[i] = { ...field, fallback: e.target.value };
                    onChange(next);
                  }}
                />
              </td>
              <td>
                <button
                  type="button"
                  className="btn ghost small"
                  onClick={() => onChange(fields.filter((f) => f.key !== field.key))}
                >
                  ✕
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
